<template>
  <v-row>
    <!-- chart -->
    <v-col class="col-12">
      <chartjs-wrapper
        ref="chartJsWrapper"
        class="white"
        chartId="productTargetPricesChart"
        :chart-data="productTargetPricesChartData"
        :type="'line'"
        :options="productTargetPricesChartOptions"
        :chart-height="20"
        hover-line
        @selected="clickedChartPoint"
      >
      </chartjs-wrapper>
    </v-col>
  </v-row>
</template>

<script>
import Vue from 'vue'
import DateRangeChartMixin from '@/components-internal/mixins/DateRangeChartMixin.js'
import _ from 'lodash'
import definitions from '../../../definitions'
import convertHex from '@/classes-internal/utils/convertHex'

export default Vue.extend({
  name: 'ProductTargetPricesChart',

  mixins: [DateRangeChartMixin],

  props: {
    disableSlider: {
      required: false,
      type: Boolean,
      default: false,
    },
    selectedDefinitions: {
      required: true,
      type: Array,
    },
    productTargetPrices: {
      required: true,
      type: Array,
    },
    pointRadius: {
      required: false,
      type: Number,
      default: definitions.globalChartSettings.pointRadius,
    },
    selectedDate: {
      type: Date,
      required: false,
      default: new Date(),
    },
    setSelectedDateStorePath: {
      type: String,
      required: false,
      default: '',
    },
  },

  computed: {
    productTargetPricesChartData() {
      const linesData =
        _.groupBy(this.productTargetPrices, 'productDefinitionId') || []

      const data = {
        labels: [],
        datasets: Object.keys(linesData).map((productDefinitionId) => {
          const targetPrice = linesData[productDefinitionId]
          const selection = _.find(
            this.selectedDefinitions,
            function (selection) {
              return selection.item.id === parseInt(productDefinitionId)
            }
          )

          if (selection) {
            const backgroundColor = convertHex(selection?.color, 10)

            return {
              borderColor: selection.color,
              borderWidth: 1.5,
              backgroundColor,
              fill: '-1',
              pointRadius: this.pointRadius,
              borderWidth: 2,
              pointBorderWidth: 1,
              hoverPointBorderWidth: 2,
              pointBackgroundColor: selection.color,
              pointBorderColor: selection.color,
              data: targetPrice.map((targetPrice) => ({
                x: targetPrice.date,
                y: targetPrice.getChartFormattedTargetPrice(),
              })),
              label: selection?.item?.getTranslatedName(),
            }
          }
        }),
      }

      return data
    },
    productTargetPricesChartOptions() {
      const mainOptions = {
        layout: {
          padding: {
            right: 60,
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItems, data) => {
              return (
                data.datasets[tooltipItems.datasetIndex].label +
                ': ' +
                tooltipItems.yLabel +
                ` ${store.getters
                  .getCurrentDestinationInstance()
                  .getCurrency()}`
              )
            },
          },
        },
        annotation: {
          annotations: [
            {
              drawTime: 'afterDatasetsDraw',
              id: 'todayLine',
              type: 'line',
              mode: 'vertical',
              scaleID: 'x-axis-0',
              value: new Date(),
              borderColor: definitions.colors.named.value,
              borderWidth: 1,
              borderDash: [5, 5],
              borderDashOffset: 0,
              label: {
                backgroundColor: definitions.colors.named.value,
                content: i18n.t('general.today'),
                enabled: true,
                fontSize: 10,
                position: 'top',
              },
            },
          ],
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString:
                  this.$t('productTargetPriceChart.ylabel') +
                  ` (${store.getters
                    .getCurrentDestinationInstance()
                    .getCurrency()})`,
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        legendCallback: (chart) => {
          const renderLabels = (chart) => {
            const { data } = chart
            return data.datasets
              .map(
                (dataset, i) => `
              <li>
                    <div id="legend-${i}-item" class="legend-item">
                      <span class="legend-marker" style="background-color:
                        ${dataset.borderColor}">
                        &nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                      ${
                        dataset.label &&
                        `<span class="label">${dataset.label}</span>`
                      }
                    </div>
                </li>
            `
              )
              .join('')
          }
          return `
          <ul class="chartjs-legend">
            ${renderLabels(chart)}
          </ul>`
        },
      }
      if (this.selectedDate) {
        mainOptions.annotation.annotations.push({
          drawTime: 'afterDatasetsDraw',
          id: 'selectionLine',
          type: 'line',
          mode: 'vertical',
          scaleID: 'x-axis-0',
          value: this.selectedDate,
          borderColor: definitions.colors.named.seasonality,
          borderWidth: 1,
          borderDash: [5, 5],
          borderDashOffset: 0,
        })
      }

      return _.merge(this.baseChartOptions, mainOptions)
    },
  },

  watch: {
    // needed to update the vertical selcted date line
    productTargetPricesChartOptions() {
      this.$refs.chartJsWrapper.reCreateChart()
    },
  },

  methods: {
    clickedChartPoint(event) {
      if (event?.label) {
        this.$store.commit(
          this.setSelectedDateStorePath,
          new Date(event?.label)
        )
      }
    },
  },
})
</script>
